/** Rows mounting this soon after they were added flash in. */
const NEW_ROW_WINDOW_MS = 400;

/** A drop's reorder animates if the moved rows render within this window. */
const REORDER_WINDOW_MS = 1000;

/**
 * More additions than this at once read as a swap (a view or filter change
 * that widens the set), not rows someone added: a created row and its new
 * group are two.
 */
const MAX_NEW_ROWS = 3;

/**
 * Changes this soon after the first rows arrived are the load settling (a
 * follow-up query swapping row ids before rows were even measured), not
 * additions the user should notice.
 */
const SETTLE_WINDOW_MS = 1000;

/**
 * An addition may replace one row (a draft row saved as the real one); more
 * removals mean the set was swapped, as a view or filter change does.
 */
const MAX_REMOVED_ROWS = 1;

/**
 * Non-reactive bookkeeping for row motion: which rows the last data change
 * added (they flash once as they mount) and whether the next layout should
 * slide rows to their new places (after a drop).
 */
export interface RowMotionTracker {
    /**
     * Call while rendering, before rows mount; reads the ids only when the
     * data array changed, so repeat calls are cheap and idempotent.
     */
    noteData: (input: {
        data: object;
        getRowIds: () => readonly string[];
    }) => void;
    /**
     * Rows are loading (skeleton, error, refetch, or a load-more batch): the
     * next data change is that load, so its rows don't flash, unless a settled
     * render sees the same data first. Call it before that render's `noteData`.
     */
    noteLoading: () => void;
    /**
     * The grid rendered idle (ready, not refetching or loading more). Only a
     * settled render with unchanged data ends a pending load; renders that
     * note neither (a scroll re-rendering the body) leave it pending.
     */
    noteSettled: () => void;
    isNewRow: (rowId: string) => boolean;
    /**
     * The next row moves slide, except the dropped row: the drag preview
     * lands on it instead. A sort passes `null`; with server sorting its rows
     * arrive renders later.
     */
    requestReorder: (droppedRowId: string | null) => void;
    isReorderRequested: () => boolean;
    getDroppedRowId: () => string | null;
    clearReorderRequest: () => void;
}

export const createRowMotionTracker = (): RowMotionTracker => {
    let lastData: object | null = null;
    let knownIds: ReadonlySet<string> = new Set();
    let newIds: ReadonlySet<string> = new Set();
    let notedAt = Number.NEGATIVE_INFINITY;
    let firstRowsAt = Number.NEGATIVE_INFINITY;
    let isLoadPending = false;
    let isSettledNoted = false;
    let reorderRequestedAt = Number.NEGATIVE_INFINITY;
    let droppedRowId: string | null = null;

    return {
        noteData: ({ data, getRowIds }) => {
            const isSettledThisRender = isSettledNoted;
            isSettledNoted = false;
            if (data === lastData) {
                isLoadPending = isLoadPending && !isSettledThisRender;
                return;
            }
            lastData = data;
            const ids = getRowIds();
            const previous = knownIds;
            knownIds = new Set(ids);
            const now = performance.now();
            if (previous.size === 0 || isLoadPending) {
                isLoadPending = false;
                firstRowsAt = ids.length > 0 ? now : firstRowsAt;
                return;
            }
            if (now - firstRowsAt < SETTLE_WINDOW_MS) {
                return;
            }
            const added = ids.filter((id) => !previous.has(id));
            const keptCount = ids.length - added.length;
            const removedCount = previous.size - keptCount;
            if (
                added.length > 0 &&
                added.length <= MAX_NEW_ROWS &&
                keptCount > 0 &&
                removedCount <= MAX_REMOVED_ROWS
            ) {
                newIds = new Set(added);
                notedAt = now;
            }
        },
        noteLoading: () => {
            isLoadPending = true;
            isSettledNoted = false;
        },
        noteSettled: () => {
            isSettledNoted = true;
        },
        isNewRow: (rowId) =>
            performance.now() - notedAt < NEW_ROW_WINDOW_MS &&
            newIds.has(rowId),
        requestReorder: (rowId) => {
            reorderRequestedAt = performance.now();
            droppedRowId = rowId;
        },
        isReorderRequested: () =>
            performance.now() - reorderRequestedAt < REORDER_WINDOW_MS,
        getDroppedRowId: () => droppedRowId,
        clearReorderRequest: () => {
            reorderRequestedAt = Number.NEGATIVE_INFINITY;
            droppedRowId = null;
        },
    };
};
